import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as z from 'zod';

import { useChangePricingPlanCard } from 'src/features/cards/api/useChangePricingPlanCard';
import { LIST_CARDS_IN_PARKING_RESOURCE } from 'src/features/cards/api/useListCardsInParking';
import ChangePricingPlanCardForm from 'src/features/cards/forms/ChangePricingPlanCardForm';
import { cardMessages } from 'src/features/cards/utils/messages';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { useListPricingPlans } from 'src/features/pricingPlans/api/useListPricingPlans';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { InParkingCard } from 'src/features/cards/types/card';

const changePricingPlanCardFormSchema = z.object({
    pricing_plan_id: z.string().min(1),
});
type ChangePlanPricingPlanCardFormValues = z.infer<typeof changePricingPlanCardFormSchema>;

interface ChangePricingPlanCardModalFormProps extends React.HTMLAttributes<HTMLDivElement> {
    inParkingCardDetail?: InParkingCard | null;
    refetch: () => void;
}

const ChangePricingPlanCardModalForm = ({
    children,
    inParkingCardDetail,
    refetch,
}: ChangePricingPlanCardModalFormProps) => {
    if (!inParkingCardDetail) return null;
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const cardType = inParkingCardDetail.card_type;
    const { data: pricingPlanData } = useListPricingPlans({ parkingId, cardType, limit: 100 });

    const pricingPlanOptions = pricingPlanData?.results.map((pricingPlan) => ({
        label: pricingPlan.plan_name,
        value: pricingPlan.id.toString(),
    }));

    const { mutate: changePricingPlanCard, isPending } = useChangePricingPlanCard({
        onSuccess: (card, data) => {
            refetch();
            onOpenChange(false);
            void queryClient.invalidateQueries({
                queryKey: [LIST_CARDS_IN_PARKING_RESOURCE],
            });
            toast({
                title: formatMessage(cardMessages.changePlanSuccess),
                description: formatMessage(cardMessages.changePlanSuccessDetails, {
                    client: card.card_number,
                    plan: pricingPlanData?.results.find((plan) => plan.id.toString() === data.pricing_plan_id)
                        ?.plan_name,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const form = useForm<ChangePlanPricingPlanCardFormValues>({
        resolver: zodResolver(changePricingPlanCardFormSchema),
        defaultValues: {
            pricing_plan_id: undefined,
        },
    });

    const onSubmit = (values: ChangePlanPricingPlanCardFormValues) => {
        const submitData = {
            parkingId,
            cardId: inParkingCardDetail.id,
            ...values,
        };

        changePricingPlanCard(submitData);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        form.reset({
            pricing_plan_id: inParkingCardDetail?.charge_plan_id ? inParkingCardDetail?.charge_plan_id.toString() : '',
        });
        setIsModalOpen(isOpen);
    };

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children}
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={ICON_MAP.EDIT} />
                        <div>
                            <DialogTitle>{formatMessage(cardMessages.changePlanCient)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(cardMessages.changePlanClientDescription, {
                                    client: inParkingCardDetail.card_number,
                                })}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <ChangePricingPlanCardForm
                        pricingPlanOptions={pricingPlanOptions}
                        form={form}
                        onSubmit={onSubmit}
                    />
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>

                    <Button
                        disabled={isPending || !form.formState.isValid}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(cardMessages.changePlan)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { ChangePricingPlanCardModalForm, changePricingPlanCardFormSchema };
export type { ChangePlanPricingPlanCardFormValues };
